/**
 * `<policy-typed-value>` — the one typed scalar editor (`03` §3.1).
 *
 * It is shared by `Predicate.value` / `values`, `ClaimCheck.value` / `values` and
 * the constant mask argument, and the type selector in front of the input is not
 * polish: **comparison is type-strict everywhere.** `1` is not `"1"` and `true` is
 * not `"true"`; only int, float and `json.Number` coerce across each other. A
 * value editor without an explicit type is an editor that produces claim checks
 * which compile and never match.
 *
 * The type is a property of the form, not of the document. `policymodel.TypedValue`
 * marshals to a **bare** scalar because Sluice's field is an `any` and a tagged
 * object there would compare against nothing, so the tag is recovered from the
 * JSON shape on the way back in — which is what `inferValueType` mirrors.
 *
 * Template mode is offered **only where templates are legal**, which is the
 * row-filter predicate value. Where it is offered, it is delegated to
 * `<policy-template-picker>`: a template must span the whole string, so there is no
 * text field with interpolation to fall back to.
 *
 * In list mode the type selector governs the whole list. `Between` requires exactly
 * two values *of the same type* and `In` compares one element at a time against a
 * type-strict operator, so a per-element type would only let an author build a list
 * where half the members can never match.
 */

import { LitElement, css, html, nothing } from "lit";
import { customElement, property, state } from "lit/decorators.js";

import "../../components/index.js";
import type {
  BnDiagnosticSeverity,
  BnSelectChangeDetail,
  BnSelectOption,
} from "../../components/index.js";
import type { BindingSummary } from "./bindings.js";
import {
  coerceValue,
  inferValueType,
  isTemplate,
  isWholeStringTemplate,
  valueText,
  type ScalarValue,
  type ValueTypeName,
} from "./model.js";
import "./policy-template-picker.js";
import type { TemplatePickerChangeDetail } from "./policy-template-picker.js";

/** Detail of `value-change`. Both fields are always present; read the one that
 * matches the element's `multiple` flag. */
export interface TypedValueChangeDetail {
  value: ScalarValue | undefined;
  values: ScalarValue[];
  /** The binding a template was built against, when the change came from the
   * template picker. */
  binding?: string;
}

/** The two booleans, as a select. They are not read from the schema because
 * `bool` has no enum entry — it is the type itself, and `true`/`false` is the
 * whole vocabulary. */
const BOOLS: BnSelectOption[] = [
  { value: "true", label: "true" },
  { value: "false", label: "false" },
];

@customElement("policy-typed-value")
export class PolicyTypedValue extends LitElement {
  @property() label = "Value";
  /** Help text for the field, from the form schema. */
  @property() help = "";
  /** The `ValueType` enum rendered as options, from the form schema. */
  @property({ attribute: false }) typeOptions: BnSelectOption[] = [];
  /** The vocabulary's own statement — that the type is part of the value. */
  @property() typesHelp = "";
  /** The single value. */
  @property({ attribute: false }) value: ScalarValue | undefined = undefined;
  /** The list, in list mode. */
  @property({ attribute: false }) values: ScalarValue[] = [];
  /** List mode. */
  @property({ type: Boolean }) multiple = false;
  /** Exactly this many entries are required, or 0 for "at least `minItems`".
   * `Between` sets 2, low first. */
  @property({ type: Number }) exactly = 0;
  @property({ type: Number }) minItems = 1;
  /** Whether template mode may be chosen at all. */
  @property({ type: Boolean, attribute: "allow-template" }) allowTemplate = false;
  /** Template picker inputs, forwarded. */
  @property({ attribute: false }) bindings: BindingSummary[] = [];
  @property() binding = "";
  @property({ type: Number }) bindingTotal = 0;
  @property() claimsHelp = "";
  /** A diagnostic to render on the field, from the linter or a local fault. */
  @property() diagnostic = "";
  @property() rule = "";
  @property() severity: BnDiagnosticSeverity = "error";
  @property({ type: Boolean }) disabled = false;

  /**
   * The type the author last chose explicitly.
   *
   * It exists for exactly one case and is otherwise ignored. Choosing template mode
   * clears the value — a literal is not a path — and an empty string infers as
   * `string`, which would close the picker the instant it opened. So the choice is
   * remembered until a value arrives that contradicts it.
   */
  @state() private chosenType: ValueTypeName | "" = "";

  static override styles = css`
    :host {
      display: block;
      font-family: var(--bino-font-sans);
      color: var(--bino-text);
    }
    .row {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
    }
    .type {
      flex: 0 0 11rem;
    }
    .input {
      flex: 1 1 auto;
      min-width: 0;
    }
    .stack {
      display: grid;
      gap: var(--bino-space-xs);
    }
    .entry {
      display: flex;
      align-items: flex-start;
      gap: var(--bino-space-sm);
    }
    .entry .input {
      flex: 1 1 auto;
    }
    p {
      margin: var(--bino-space-xs) 0 0;
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      max-width: 80ch;
    }
    .ordinal {
      font-size: var(--bino-font-size-sm);
      color: var(--bino-text-secondary);
      padding-top: 0.5rem;
      white-space: nowrap;
    }
    bn-field {
      margin-bottom: 0;
    }
    .picker {
      margin-top: var(--bino-space-sm);
    }
  `;

  /** The authored type. It is derived from the value rather than held as state, so
   * a document loaded from the API and a document just edited agree — the wire
   * carries no tag, and a remembered type would drift from the value it describes. */
  private get type(): ValueTypeName {
    const inferred = this.multiple ? inferValueType(this.values[0]) : inferValueType(this.value);
    if (this.chosenType === "template" && inferred === "string") return "template";
    return inferred;
  }

  /** Drops the remembered choice as soon as a value arrives that contradicts it, so a
   * reused element cannot carry template mode onto a node holding a literal. */
  override willUpdate(changed: Map<string, unknown>) {
    if (this.chosenType !== "template" || !changed.has("value")) return;
    const raw = this.value;
    if (typeof raw !== "string" || (raw !== "" && !isTemplate(raw))) this.chosenType = "";
  }

  /** The type options, minus template where templates are not legal. Filtering
   * here rather than at the call site keeps the "template only in a row filter"
   * rule in one place. */
  private get types(): BnSelectOption[] {
    if (!this.allowTemplate) return this.typeOptions.filter((o) => o.value !== "template");
    if (!this.multiple) return this.typeOptions;
    // A list of templates is legal upstream, and the picker emits ONE whole-string
    // value — so in list mode the entries are edited as text and the whole-string rule
    // is checked rather than composed. The option says so instead of disappearing,
    // because an imported list may already hold one.
    return this.typeOptions.map((o) =>
      o.value === "template"
        ? {
            ...o,
            description:
              "The binding-aware picker is available for a single value only. In a list each entry is typed as text and still has to be a whole-string reference.",
          }
        : o,
    );
  }

  private emit(detail: Partial<TypedValueChangeDetail>) {
    this.dispatchEvent(
      new CustomEvent<TypedValueChangeDetail>("value-change", {
        detail: {
          value: this.value,
          values: this.values,
          ...detail,
        },
        bubbles: true,
        composed: true,
      }),
    );
  }

  /**
   * Switching the type re-coerces what is already there rather than clearing it,
   * because the common case is an author who typed `42` and then said it was a
   * number. The exceptions are the two types with no text at all: `null` carries no
   * value, and `template` must be re-picked because a literal is not a path.
   */
  private onType(e: CustomEvent<BnSelectChangeDetail>) {
    e.stopPropagation();
    const type = e.detail.value;
    this.chosenType = type;
    if (this.multiple) {
      const next =
        type === "null"
          ? this.values.map(() => null)
          : this.values.map((v) => coerceValue(type, valueText(v)));
      this.emit({ values: next });
      return;
    }
    if (type === "template") {
      this.emit({ value: "" });
      return;
    }
    this.emit({ value: coerceValue(type, valueText(this.value)) });
  }

  private onSingle(text: string) {
    this.emit({ value: coerceValue(this.type, text) });
  }

  private onEntry(index: number, text: string) {
    const next = [...this.values];
    const coerced = coerceValue(this.type, text);
    if (index < next.length) next[index] = coerced;
    else next.push(coerced);
    this.emit({ values: next });
  }

  private removeEntry(index: number) {
    this.emit({ values: this.values.filter((_, i) => i !== index) });
  }

  private renderTypeSelect() {
    return html`<div class="type">
      <bn-select
        aria-label=${`${this.label} type`}
        .options=${this.types}
        .value=${this.type}
        ?disabled=${this.disabled}
        @change=${this.onType}
      ></bn-select>
    </div>`;
  }

  /** One scalar input, typed. `null` renders no control at all: an explicit null
   * is a value, and offering a text box for it would invite a literal "null". */
  private renderInput(current: ScalarValue | undefined, onText: (t: string) => void, label: string) {
    const type = this.type;
    if (type === "null") {
      return html`<div class="input">
        <p>An explicit <code>null</code>, which is not the same as leaving the field out.</p>
      </div>`;
    }
    if (type === "bool") {
      return html`<div class="input">
        <bn-select
          aria-label=${label}
          .options=${BOOLS}
          .value=${current === true ? "true" : "false"}
          ?disabled=${this.disabled}
          @change=${(e: CustomEvent<BnSelectChangeDetail>) => {
            e.stopPropagation();
            onText(e.detail.value);
          }}
        ></bn-select>
      </div>`;
    }
    if (type === "template" && !this.multiple) {
      // Template mode replaces the input entirely; the picker is rendered below.
      return nothing;
    }
    return html`<div class="input">
      <bn-input
        mono
        aria-label=${label}
        inputmode=${type === "int" ? "numeric" : type === "float" ? "decimal" : "text"}
        .value=${valueText(current)}
        ?disabled=${this.disabled}
        @input=${(e: CustomEvent<{ value: string }>) => {
          e.stopPropagation();
          onText(e.detail.value);
        }}
      ></bn-input>
    </div>`;
  }

  private renderTemplate() {
    // The picker composes one whole-string value, so it belongs to single mode only.
    if (this.type !== "template" || this.multiple) return nothing;
    return html`<div class="picker">
      <policy-template-picker
        .bindings=${this.bindings}
        .binding=${this.binding}
        .bindingTotal=${this.bindingTotal}
        .claimsHelp=${this.claimsHelp}
        .value=${valueText(this.value)}
        @template-change=${(e: CustomEvent<TemplatePickerChangeDetail>) => {
          e.stopPropagation();
          this.emit({ value: e.detail.value, binding: e.detail.binding });
        }}
      ></policy-template-picker>
    </div>`;
  }

  /** The list rows, plus one blank row, plus the count statement the operator
   * demands. `Between` is the one that needs the ordinals: two values, low first. */
  private renderList() {
    const rows = this.disabled ? this.values : [...this.values, undefined];
    const ordered = this.exactly === 2;
    return html`<div class="stack">
      ${rows.map((v, i) => {
        const last = i === this.values.length;
        return html`<div class="entry">
          ${ordered && i < 2
            ? html`<span class="ordinal">${i === 0 ? "low" : "high"}</span>`
            : nothing}
          ${this.renderInput(
            v,
            (t) => this.onEntry(i, t),
            `${this.label} ${i + 1}`,
          )}
          ${!this.disabled && !last
            ? html`<bn-button
                size="sm"
                variant="ghost"
                @click=${() => this.removeEntry(i)}
                aria-label=${`Remove ${this.label} ${i + 1}`}
              >
                Remove
              </bn-button>`
            : nothing}
        </div>`;
      })}
    </div>`;
  }

  /**
   * The partial-template fault (`L13`).
   *
   * The picker structurally cannot produce one — it emits the whole string — so this
   * fires only for an IMPORTED value, which is exactly when it is needed: Sluice
   * recognises any string containing `{{` and `}}` as a template and then refuses it
   * for not spanning the whole value, so `"t-{{ subject.sub }}"` fails the snapshot
   * compile rather than being taken as a literal.
   */
  private get templateFault(): string {
    if (this.type !== "template") return "";
    const raws = this.multiple ? this.values.map(valueText) : [valueText(this.value)];
    if (raws.every((raw) => raw === "" || isWholeStringTemplate(raw))) return "";
    return "A template must span the whole value. Sluice recognises any string containing {{ and }} as a template and then refuses this one at compile, so the snapshot fails to load and the instance keeps serving its previous policy. Pick an attribute below to replace the whole value.";
  }

  /** The count fault, stated locally because it is instant and the server would
   * say the same thing one round trip later. */
  private get countFault(): string {
    if (!this.multiple) return "";
    const n = this.values.length;
    if (this.exactly > 0 && n !== this.exactly) {
      return `Exactly ${this.exactly} values are required${this.exactly === 2 ? ", low first" : ""}; there ${n === 1 ? "is" : "are"} ${n}.`;
    }
    if (this.exactly === 0 && n < this.minItems) {
      return `At least ${this.minItems} value${this.minItems === 1 ? "" : "s"} required.`;
    }
    return "";
  }

  override render() {
    // A local fault wins the message slot over a server finding: it is the same
    // defect said sooner, and both carry the rule id so the two read as one rule.
    const templateFault = this.templateFault;
    const fault = templateFault || this.countFault;
    const message = fault || this.diagnostic;
    return html`
      <bn-field
        label=${this.label}
        hint=${this.help}
        error=${message}
        rule=${fault ? (templateFault ? "L13" : "") : this.rule}
        severity=${fault ? "error" : this.severity}
      >
        <div>
          <div class="row">
            ${this.renderTypeSelect()}
            ${this.multiple
              ? html`<div class="input">${this.renderList()}</div>`
              : this.renderInput(this.value, (t) => this.onSingle(t), this.label)}
          </div>
          ${this.renderTemplate()}
          ${this.typesHelp ? html`<p>${this.typesHelp}</p>` : nothing}
        </div>
      </bn-field>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    "policy-typed-value": PolicyTypedValue;
  }
}
